import * as React from 'react'

import { useSplitEnabled } from '@buffer-mono/features'
import { useLocalStorage } from '@buffer-mono/popcorn'

import { useQueryParam } from '~publish/hooks/useQueryParam'

import { DEFAULT_STATUS } from './types'
import type { FilterStatus } from './types'
import {
  COMMENTS_STATUS_FILTER_STORAGE_KEY,
  PERSIST_STATUS_FILTER_SPLIT,
} from './usePersistedStatusParam'

/**
 * Owns the side effects that keep the persisted status filter and the URL in
 * sync. Mounted exactly once per Community view (AllChannelsView,
 * SingleChannelView).
 *
 * These effects deliberately live in a dedicated single-mount component
 * rather than in `usePersistedStatusParam`: hooks that read filter state are
 * called from many components at once, and each instance running its own
 * sync effect would fire duplicate navigations from possibly stale URL
 * snapshots. Keeping the read hooks effect-free is what makes them safe to
 * call from anywhere.
 *
 * - On entry without a `?status` param, a stored preference is mirrored to
 *   the URL (replace, no history entry) so the visible URL stays shareable.
 * - When the URL param changes (e.g. arriving via a shared link), it is
 *   persisted so the next fresh visit restores what the user last saw.
 */
export function PersistedStatusSync(): null {
  const { isEnabled } = useSplitEnabled(PERSIST_STATUS_FILTER_SPLIT)
  const [statusParam, setStatusParam] = useQueryParam<FilterStatus>('status')
  const [persistedStatus, setPersistedStatus] = useLocalStorage<
    FilterStatus | undefined
  >(COMMENTS_STATUS_FILTER_STORAGE_KEY, undefined)

  // On mount, mirror a real stored preference to the URL when no URL param is
  // present (e.g. user navigated back without a deep link). Skips undefined and
  // DEFAULT_STATUS to avoid injecting a redundant ?status=all into the URL.
  const initializedRef = React.useRef(false)
  const shouldSyncPersistedStatusToUrl =
    isEnabled &&
    !initializedRef.current &&
    !statusParam &&
    persistedStatus !== undefined &&
    persistedStatus !== DEFAULT_STATUS
  React.useEffect(() => {
    if (shouldSyncPersistedStatusToUrl) {
      initializedRef.current = true
      setStatusParam(persistedStatus, { replace: true })
    }
  }, [shouldSyncPersistedStatusToUrl, setStatusParam, persistedStatus])

  // Keep localStorage in sync when the status changes via the URL directly
  // (e.g. user arrives via a shared link) so the next fresh visit restores
  // what they last saw rather than their previous stored preference.
  React.useEffect(() => {
    if (isEnabled && statusParam) {
      setPersistedStatus(statusParam)
    }
  }, [isEnabled, statusParam, setPersistedStatus])

  return null
}
